import { useState } from "react";
import { 
  Plus, Search, Edit, Trash2, 
  Loader2,
  MoreHorizontal
} from "lucide-react";
import { 
  useGetCouponsQuery, 
  useCreateCouponMutation, 
  useUpdateCouponMutation, 
  useDeleteCouponMutation, 
  useGetCategoriesQuery,
  type Coupon,
  type Category
} from "@/store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  Card, 
  CardContent, 
  CardHeader 
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { format } from "date-fns";

export function AdminCouponsScreen() {
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);

  const { data: couponsData, isLoading: isLoadingCoupons } = useGetCouponsQuery();
  const { data: categoriesData } = useGetCategoriesQuery();
  
  const [createCoupon, { isLoading: isCreating }] = useCreateCouponMutation();
  const [updateCoupon, { isLoading: isUpdating }] = useUpdateCouponMutation();
  const [deleteCoupon] = useDeleteCouponMutation();

  const coupons = couponsData?.data || [];
  const categories = categoriesData?.data || [];

  const filteredCoupons: Coupon[] = coupons.filter((coupon: Coupon) => 
    coupon.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const [formData, setFormData] = useState<any>({
    code: "",
    discountType: "PERCENTAGE",
    discountValue: 0,
    minPurchase: 0,
    maxDiscount: 0,
    validFrom: format(new Date(), "yyyy-MM-dd"),
    validUntil: format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), "yyyy-MM-dd"),
    usageLimit: 0,
    usageLimitPerUser: 1,
    isFirstOrderOnly: false,
    isFreeDelivery: false,
    isActive: true,
    applicableCategoryIds: [],
    applicableProductIds: []
  });

  const handleOpenDialog = (coupon: Coupon | null = null) => {
    if (coupon) {
      setEditingCoupon(coupon);
      setFormData({
        code: coupon.code,
        discountType: coupon.discountType,
        discountValue: Number(coupon.discountValue),
        minPurchase: Number(coupon.minPurchase || 0),
        maxDiscount: Number(coupon.maxDiscount || 0),
        validFrom: format(new Date(coupon.validFrom), "yyyy-MM-dd"),
        validUntil: format(new Date(coupon.validUntil), "yyyy-MM-dd"),
        usageLimit: coupon.usageLimit || 0,
        usageLimitPerUser: coupon.usageLimitPerUser || 1,
        isFirstOrderOnly: coupon.isFirstOrderOnly,
        isFreeDelivery: coupon.isFreeDelivery,
        isActive: coupon.isActive,
        applicableCategoryIds: coupon.applicableCategoryIds || [],
        applicableProductIds: coupon.applicableProductIds || []
      });
    } else {
      setEditingCoupon(null);
      setFormData({
        code: "",
        discountType: "PERCENTAGE",
        discountValue: 0,
        minPurchase: 0,
        maxDiscount: 0,
        validFrom: format(new Date(), "yyyy-MM-dd"),
        validUntil: format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), "yyyy-MM-dd"),
        usageLimit: 0,
        usageLimitPerUser: 1,
        isFirstOrderOnly: false,
        isFreeDelivery: false,
        isActive: true,
        applicableCategoryIds: [],
        applicableProductIds: []
      });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const data = {
      ...formData,
      discountValue: Number(formData.discountValue),
      usageLimit: Number(formData.usageLimit) > 0 ? Number(formData.usageLimit) : null,
      usageLimitPerUser: Number(formData.usageLimitPerUser),
      minPurchase: Number(formData.minPurchase) > 0 ? Number(formData.minPurchase) : null,
      maxDiscount: Number(formData.maxDiscount) > 0 ? Number(formData.maxDiscount) : null,
      validFrom: new Date(formData.validFrom).toISOString(),
      validUntil: new Date(formData.validUntil).toISOString(),
    };

    try {
      if (editingCoupon) {
        await updateCoupon({ code: editingCoupon.code, data }).unwrap();
        toast.success("Coupon updated successfully");
      } else {
        await createCoupon(data).unwrap();
        toast.success("Coupon created successfully");
      }
      setIsDialogOpen(false);
    } catch (error: any) {
      toast.error(error?.data?.error || "Failed to save coupon");
    }
  };

  const handleDelete = async (code: string) => {
    if (window.confirm("Are you sure you want to delete this coupon?")) {
      try {
        await deleteCoupon(code).unwrap();
        toast.success("Coupon deleted successfully");
      } catch (error: any) {
        toast.error(error?.data?.error || "Failed to delete coupon");
      }
    }
  };

  const formatPrice = (price: number) => {
    return `₦${price.toLocaleString()}`;
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Coupons</h1>
          <p className="text-muted-foreground">
            Manage promotion codes and marketing discounts.
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="h-4 w-4 mr-2" />
          Create Coupon
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by code..."
                className="pl-9"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="text-sm text-muted-foreground">
              Showing {filteredCoupons.length} coupons
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Value</TableHead>
                  <TableHead>Usage</TableHead>
                  <TableHead>Validity</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoadingCoupons ? (
                  <TableRow>
                    <TableCell colSpan={6} className="h-24 text-center">
                      <div className="flex items-center justify-center">
                        <Loader2 className="h-6 w-6 animate-spin mr-2" />
                        Loading coupons...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filteredCoupons.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="h-24 text-center">
                      No coupons found.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCoupons.map((coupon: Coupon) => {
                    const isExpired = new Date(coupon.validUntil) < new Date();
                    const isExhausted = coupon.usageLimit && coupon.usedCount >= coupon.usageLimit;
                    
                    return (
                      <TableRow key={coupon.code}>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-bold tracking-wider">{coupon.code}</span>
                            {coupon.isFreeDelivery && (
                              <Badge variant="secondary" className="w-fit text-[10px] h-4 px-1 mt-1 font-normal bg-green-50 text-green-700 border-green-200">
                                Free Delivery
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {coupon.discountType === "PERCENTAGE" ? (
                            <span>{coupon.discountValue}% off</span>
                          ) : (
                            <span>{formatPrice(Number(coupon.discountValue))} off</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="text-sm font-medium">{coupon.usedCount}</div>
                            {coupon.usageLimit && (
                              <div className="text-xs text-muted-foreground">
                                / {coupon.usageLimit}
                              </div>
                            )}
                            <div className="h-1.5 w-12 rounded-full bg-muted overflow-hidden">
                              <div 
                                className={cn(
                                  "h-full bg-primary",
                                  isExhausted && "bg-destructive"
                                )}
                                style={{ width: `${Math.min(100, (coupon.usedCount / (coupon.usageLimit || 1)) * 100)}%` }}
                              />
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col text-xs space-y-0.5">
                            <span className="text-muted-foreground">Ends {format(new Date(coupon.validUntil), "MMM dd, yyyy")}</span>
                            {isExpired && (
                              <span className="text-destructive font-medium">Expired</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {coupon.isActive && !isExpired && !isExhausted ? (
                            <Badge className="bg-green-100 text-green-700 border-green-200 hover:bg-green-100">Active</Badge>
                          ) : (
                            <Badge variant="outline" className="text-muted-foreground">Inactive</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              <DropdownMenuItem onClick={() => handleOpenDialog(coupon)}>
                                <Edit className="h-4 w-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                className="text-destructive"
                                onClick={() => handleDelete(coupon.code)}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCoupon ? "Edit Coupon" : "Create New Coupon"}</DialogTitle>
            <DialogDescription>
              Configure discount rules and validity for this promo code.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-3 gap-6">
              <div className="space-y-2 col-span-1">
                <Label htmlFor="code">Coupon Code</Label>
                <Input
                  id="code"
                  placeholder="NEWYEAR2024"
                  className="uppercase font-mono text-lg font-bold tracking-widest"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  disabled={!!editingCoupon}
                  required
                />
              </div>
              <div className="col-span-2 flex items-center gap-8 pl-4 border-l">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="isActive"
                    checked={formData.isActive}
                    onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                  />
                  <Label htmlFor="isActive" className="cursor-pointer">Active Status</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="isFreeDelivery"
                    checked={formData.isFreeDelivery}
                    onCheckedChange={(checked) => setFormData({ ...formData, isFreeDelivery: checked })}
                  />
                  <Label htmlFor="isFreeDelivery" className="cursor-pointer">Free Delivery</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="firstOrderOnly"
                    checked={formData.isFirstOrderOnly}
                    onCheckedChange={(checked) => setFormData({ ...formData, isFirstOrderOnly: !!checked })}
                  />
                  <Label htmlFor="firstOrderOnly" className="cursor-pointer">First Order Only</Label>
                </div>
              </div>
            </div>

            <div className="space-y-4 border p-4 rounded-lg bg-accent/30">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-tight">Discount Rules</h3>
              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select
                    value={formData.discountType}
                    onValueChange={(val) => setFormData({ ...formData, discountType: val })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PERCENTAGE">Percentage (%)</SelectItem>
                      <SelectItem value="FIXED">Fixed Amount (₦)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Value</Label>
                  <Input
                    type="number"
                    value={formData.discountValue}
                    onChange={(e) => setFormData({ ...formData, discountValue: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Min Purchase (₦)</Label>
                  <Input
                    type="number"
                    value={formData.minPurchase}
                    onChange={(e) => setFormData({ ...formData, minPurchase: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Max Discount (₦)</Label>
                  <Input
                    type="number"
                    value={formData.maxDiscount}
                    onChange={(e) => setFormData({ ...formData, maxDiscount: e.target.value })}
                    disabled={formData.discountType === "FIXED"}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-4 border p-4 rounded-lg">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-tight">Validity & Limits</h3>
              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Valid From</Label>
                  <Input
                    type="date"
                    value={formData.validFrom}
                    onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Valid Until</Label>
                  <Input
                    type="date"
                    value={formData.validUntil}
                    onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Total Limit</Label>
                  <Input
                    type="number"
                    placeholder="Unlimited"
                    value={formData.usageLimit}
                    onChange={(e) => setFormData({ ...formData, usageLimit: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Per User Limit</Label>
                  <Input
                    type="number"
                    value={formData.usageLimitPerUser}
                    onChange={(e) => setFormData({ ...formData, usageLimitPerUser: e.target.value })}
                    required
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2 border-t pt-4">
              <Label>Applicable Categories (Optional - Leave empty for all)</Label>
              <div className="flex flex-wrap gap-2 p-3 border rounded-md bg-background min-h-[50px]">
                {categories.map((cat: Category) => (
                    <Badge
                      key={cat.id}
                      variant={formData.applicableCategoryIds.includes(cat.id) ? "default" : "outline"}
                      className="cursor-pointer"
                      onClick={() => {
                        const newIds = formData.applicableCategoryIds.includes(cat.id)
                          ? formData.applicableCategoryIds.filter((id: string) => id !== cat.id)
                          : [...formData.applicableCategoryIds, cat.id];
                        setFormData({ ...formData, applicableCategoryIds: newIds });
                      }}
                    >
                      {cat.name}
                    </Badge>
                  ))}
                  {categories.length === 0 && <span className="text-xs text-muted-foreground">No categories available</span>}
                </div>
              </div>

            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsDialogOpen(false)}
                disabled={isCreating || isUpdating}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isCreating || isUpdating}>
                {(isCreating || isUpdating) && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {editingCoupon ? "Update Coupon" : "Create Coupon"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Helper function for classNames
function cn(...classes: any[]) {
  return classes.filter(Boolean).join(' ');
}
